import React, { useCallback, useEffect } from 'react';
import { AppDispatch, connect, ConnectedProps, DefaultRootState, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { bindActionCreators } from 'redux';

import { MetaTags, SplashScreen } from 'common/components';
import { useWeb3 } from 'common/components/Web3/hooks/useWeb3.hook';

import { AXIOS_INTERCEPTOR_EVENT_NAME } from 'common/constants';
import { useCustomer, useGtm, useI18n, useLocationHistory, usePopup, useTracking } from 'common/hooks';
import NotificationContainer from 'common/hooks/useNotification/Notification.container';
import { SessionExpirationPopup } from 'common/popups';
import * as resourcesActions from 'common/redux/resources/actions';
import * as userActions from 'common/redux/user/actions';

import * as assetsActions from 'components/AssetsList/redux/assets.actions';

import * as exchangeActions from 'views/exchange/redux/exchange.actions';
import * as walletActions from 'views/wallet-new/redux/actions';

import { routes } from 'routes/routes.constants';

import { getCulture } from 'utils/culture';
import { memoizedComponent } from 'utils/memoizeComponent';

import MainLayout from 'blocks/MainLayout/MainLayout.component';

import '../../styles/index.css';

const PresaleTokenPopup = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PresaleTokenPopupContainer" */ 'common/popups/PresaleToken/PresaleTokenPopup.component'
    ),
);

const appHeight = () => {
  const doc = document.documentElement;
  doc.style.setProperty('--app-height', `${document.documentElement.clientHeight}px`);
  doc.style.setProperty('--app-width', `${document.documentElement.clientWidth}px`);
};

const Modal = React.lazy(() => import(/* webpackChunkName: "Modal" */ 'common/components/Modal/Modal.component'));

const mapStateToProps = (state: DefaultRootState) => {
  const { user, resources } = state.common;
  return {
    isAuthenticated: user.isAuthenticated,
    cryptoPayUrl: '', // user.cryptoPayUrl,
    loading: user.loading || resources.loading || state.walletReducer.currenciesLoading,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  actions: bindActionCreators(
    {
      ...assetsActions,
      ...resourcesActions,
      ...userActions,
      ...walletActions,
      ...exchangeActions,
    },
    dispatch,
  ),
});

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);

type Props = ConnectedProps<typeof reduxConnector> & {
  loading: boolean;
};

const MainLayoutContainer: React.FC<React.PropsWithChildren<Props>> = ({ actions, loading, ...props }) => {
  const { track } = useTracking();
  const { translate } = useI18n();
  const { isAuthenticated, id, settings } = useCustomer();
  const isModalOpen = useSelector((state: DefaultRootState) => state.common.modal.isModalOpen);
  const { pathname } = useLocation();
  const { push, listen } = useHistory();
  const gtm = useGtm();
  const { disconnect } = useWeb3();
  const { previousLocation, currenLocation } = useLocationHistory();
  const { show, hide } = usePopup();

  const getAppDimensions = useCallback(() => appHeight(), []);

  useEffect(() => {
    document.title = translate.toText('mobile:tab-title', true);
  }, [translate]);

  useEffect(
    function calculateScreenHeight() {
      getAppDimensions();
      window.addEventListener('resize', getAppDimensions);

      return () => window.removeEventListener('resize', getAppDimensions);
    },
    [getAppDimensions],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    actions.actionGetResources(getCulture());
    actions.actionGetLanguages();
    actions.actionGetAssets();
    actions.actionGetAssetsGroups();
    actions.actionLoadingGetCurrencies();
    actions.actionLoadingGetExchangeSettings();
  }, [actions]);

  useEffect(
    function initializeGtm() {
      const isGtmInitialized = gtm.isInitialized();
      const isGtmLoading = gtm.isLoading();

      if (settings.gtmId && !isGtmInitialized && !isGtmLoading) {
        gtm.initializeGTM(settings.gtmId, id);
      }
    },
    [gtm, settings.gtmId, id],
  );

  useEffect(() => {
    if (isAuthenticated) {
      actions.actionGetFavoriteAssets();
      actions.actionLoadingGetWallets();
    }
    if (window._authProtected && !isAuthenticated) {
      const redirectUrl = `${window.location.origin}${routes.getLoginPage()}?redirectUrl=${pathname}`;
      window.location.assign(redirectUrl);
    }
  }, [actions, isAuthenticated, pathname, push]);

  useEffect(() => {
    if (previousLocation !== currenLocation) {
      track('onPageEnter');
    }
  }, [previousLocation, currenLocation, track]);

  const onApiError = useCallback(
    async (e: Event) => {
      const event = e as CustomEvent<Error | undefined>;
      if (isAuthenticated && event.detail?.message.includes('401')) {
        try {
          sessionStorage.clear();
        } catch (_e) {
          console.log(_e);
        }
        show(SessionExpirationPopup());
        await disconnect();
      }
    },
    [disconnect, isAuthenticated, show],
  );

  useEffect(
    function handleBackendUnAuthResponseCodes() {
      window.addEventListener(AXIOS_INTERCEPTOR_EVENT_NAME, onApiError);
      return () => window.removeEventListener(AXIOS_INTERCEPTOR_EVENT_NAME, onApiError);
    },
    [onApiError],
  );

  useEffect(
    function hidePopupWhenUserClicksBackButton() {
      const unlisten = listen((_location, action) => {
        if (action === 'POP') hide();
      });
      return () => {
        // without timeout `unlisten` fires earlier than `listen` and flush our logic
        setTimeout(() => unlisten());
      };
    },
    [hide, listen],
  );

  return (
    <>
      <SplashScreen isLoading={loading} />
      <MetaTags view='root' />
      {!loading && <MainLayout {...props} />}
      <React.Suspense fallback={null}>{isModalOpen && <Modal />}</React.Suspense>
      <NotificationContainer />
      <React.Suspense fallback={null}>
        <PresaleTokenPopup />
      </React.Suspense>
    </>
  );
};

const MemoizedMainLayoutContainer = memoizedComponent(reduxConnector(MainLayoutContainer));

export default MemoizedMainLayoutContainer;
